import { useMutation, useQueryClient } from '@tanstack/react-query'

import { errorToast } from 'common/components/toastNotification'
import { useAppSelector } from 'common/hooks/redux'

import {
  CustomerAttributesResponse,
  ProfileAttributeItemResponse,
  updateCustomerAttributes,
} from '../api'
import { keys } from '../queryKeys'

type CustomerAttributeUpdateParams = {
  attributeId: string
  catalogueAttributeId: string
  region: string
  description: string
  isUsed: boolean
  isCmabUsed: boolean
}

const useUpdateCustomerAttributesMutation = () => {
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const queryClient = useQueryClient()

  return useMutation<
    ProfileAttributeItemResponse[],
    Error,
    CustomerAttributeUpdateParams,
    {
      previousData: CustomerAttributesResponse[] | undefined
      queryKey: unknown[]
    }
  >({
    mutationFn: ({
      attributeId,
      catalogueAttributeId,
      region,
      description,
      isUsed,
      isCmabUsed,
    }) =>
      updateCustomerAttributes({
        accountId,
        attributeId,
        description,
        catalogueAttributeId,
        region,
        isUsed,
        isCmabUsed,
      }),
    onMutate: async (variables) => {
      const queryKey = keys.customerAttributes({
        accountId,
        catalogueAttributeId: variables.catalogueAttributeId,
        region: variables.region,
      })

      await queryClient.cancelQueries({ queryKey })

      const previousData =
        queryClient.getQueryData<CustomerAttributesResponse[]>(queryKey)

      queryClient.setQueryData<CustomerAttributesResponse[]>(
        queryKey,
        (old) => {
          if (!old) {
            return old
          }
          return old.map((attribute) =>
            attribute.id === variables.attributeId
              ? {
                  ...attribute,
                  isUsed: variables.isUsed,
                  isCmabUsed: variables.isCmabUsed,
                }
              : attribute
          )
        }
      )

      return { previousData, queryKey }
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: keys.customerAttributes({
          accountId,
          catalogueAttributeId: variables.catalogueAttributeId,
          region: variables.region,
        }),
      })
    },
    onError: (_, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(context.queryKey, context.previousData)
      }
      errorToast('Failed to update the attribute')
    },
  })
}

export default useUpdateCustomerAttributesMutation
